import {StatusType} from '@/helpers/constants';
import {dataStatus, determineStatus, isNil} from '@/helpers/utils';

/**
 * Aggregates per-row statuses into one status value per assignee.
 * @param {{
 *   periodIds: Array<number|string>,
 *   accountData: Object<string, Object<string, Array<Object>>>,
 *   adHocData: Object<string, Array<Object>>,
 *   includeAssigneeIds?: Array<number|string|null>|null,
 *   hasActivitiesDimension?: boolean,
 *   resolveAssigneeId?: (item: Object) => (number|string|null|undefined),
 *   resolveValidProjectIds?: ((periodId: number|string) => Array<string>)|null,
 *   validActivityIds?: Array<string>|null,
 *   hasUnresolvedFlagsForAssignee?: ((assigneeId: number|string|null, periodId: number|string) => boolean)|null
 * }} params
 * @returns {Object<string, *>}
 */
export const collectStatusesByAssignee = ({
   periodIds,
   accountData,
   adHocData,
   includeAssigneeIds = null,
   hasActivitiesDimension = false,
   ignoreEmployeeNetAmountRequirement = false,
   resolveAssigneeId = (item) => item.assigneeId,
   resolveValidProjectIds = null,
   validActivityIds = null,
   hasUnresolvedFlagsForAssignee = null,
}) => {
   const result = {};
   // An assignee whose data would otherwise read Complete must not show Complete
   // while they still have an unresolved flag -- that's the same signal "Mark
   // Section Complete" already gates on, surfaced on the status badge itself.
   const flaggedAssigneeKeys = new Set();
   const includedKeys = includeAssigneeIds
      ? new Set(includeAssigneeIds.map((id) => String(id ?? null)))
      : null;

   const processItem = (item, validProjectIds, periodId) => {
      const {
         assignable,
         percentage,
         totalDebit,
         projectsData,
         activitiesData,
         vendorId,
         contractorId,
         employeeId,
         uploadFileId,
      } = item;
      const assigneeId = resolveAssigneeId(item);

      if (includedKeys && !includedKeys.has(String(assigneeId ?? null))) {
         return;
      }

      if (hasUnresolvedFlagsForAssignee?.(assigneeId, periodId)) {
         flaggedAssigneeKeys.add(String(assigneeId ?? null));
      }

      const isContractorRow = !isNil(contractorId);
      const statusTotalDebit = isContractorRow ? (assignable ? 1 : totalDebit) : totalDebit;
      const hasVendorId = !!(vendorId || contractorId || employeeId);
      // Imported employees always arrive with wages already populated from the
      // CSV -- only manually-added employees actually need that field checked
      // as a completion gate. Non-employee rows (employeeId nil) are unaffected.
      const isImportedEmployeeRow = !isNil(employeeId) && !isNil(uploadFileId);

      const status = dataStatus({
         projectsData,
         activitiesData,
         percentage: percentage?.value,
         totalDebit: statusTotalDebit,
         hasVendorId,
         hasActivitiesDimension,
         validProjectIds,
         validActivityIds,
         requiresNetAmount:
            hasVendorId && !isImportedEmployeeRow && !ignoreEmployeeNetAmountRequirement,
      });

      result[assigneeId] ??= [];
      result[assigneeId].push(status);
   };

   periodIds.forEach((periodId) => {
      const validProjectIds = resolveValidProjectIds ? resolveValidProjectIds(periodId) : null;
      const processItemForPeriod = (item) => processItem(item, validProjectIds, periodId);

      Object.entries(accountData[periodId] ?? {}).forEach(([, items]) => {
         items.forEach(processItemForPeriod);
      });

      // Deduplication of stale duplicate BaseAssignment-fanned employee rows
      // is now handled backend-side, so every row returned here is already
      // the single canonical one -- no client-side dedup needed before
      // aggregating status.
      (adHocData[periodId] ?? []).forEach(processItemForPeriod);
   });

   Object.entries(result).forEach(([assigneeId, statuses]) => {
      const merged = determineStatus(statuses);
      result[assigneeId] =
         merged === StatusType.COMPLETE && flaggedAssigneeKeys.has(String(assigneeId ?? null))
            ? StatusType.IN_PROGRESS
            : merged;
   });

   return result;
};
